/**
 * Hashes a string (user id, name, or email) into a soft pastel avatar colour.
 * Same input always returns the same colour, so a user keeps their avatar
 * appearance across the app without us needing to store anything.
 *
 * The palette is deliberately muted — avatars shouldn't compete visually
 * with the brand or with data on the page. Pure brand blue on every avatar
 * made the UI feel like a wall of logos; a soft, varied palette reads as
 * user identity instead.
 */

// `bg` is the Avatar's fill; `color` is the initials text. Raw palette steps,
// so each pair is the same in light and dark: a soft 200-level tint under its
// hue's dark text. Every pair clears WCAG AA 4.5:1 (axe on the rail's
// workspace tile, `avatarColor.test.ts`): cyan's 800 is 4.37:1 on its 200, so
// it takes 900.
export const AVATAR_PALETTE: ReadonlyArray<{ bg: string; color: string }> = [
  { bg: 'accent.200', color: 'accent.800' },
  { bg: 'teal.200', color: 'teal.800' },
  { bg: 'info.200', color: 'info.800' },
  { bg: 'warning.200', color: 'warning.800' },
  { bg: 'decorativeDeep.200', color: 'decorativeDeep.800' },
  { bg: 'success.200', color: 'success.800' },
  { bg: 'cyan.200', color: 'cyan.900' },
  { bg: 'warning.200', color: 'warning.800' },
];

function hashString(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 31 + input.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

export interface AvatarColor {
  bg: string;
  color: string;
}

/**
 * Low-level primitive — pick a stable avatar colour for an opaque key.
 *
 * Most callers should use `getAvatarColorForUser` instead, which enforces
 * the userId > email > name priority cascade so the same user gets the
 * same colour everywhere. Use this primitive only when you have already
 * resolved the canonical key (e.g. `MultiSelectOption.avatar.avatarKey`).
 *
 * Null/empty inputs get a neutral tone (bg.subtle).
 */
export function getAvatarColor(key: string | null | undefined): AvatarColor {
  if (!key) {
    return { bg: 'bg.subtle', color: 'text.secondary' };
  }
  return AVATAR_PALETTE[hashString(key) % AVATAR_PALETTE.length] as AvatarColor;
}

/**
 * Pick a stable avatar colour for a user. Enforces the priority cascade
 * (userId → email → name) at the function boundary so call sites can't
 * accidentally hash a different field for the same person.
 *
 * The same-user-same-color invariant breaks the moment two surfaces hash
 * different fields for the same user — sidebar profile would render purple
 * while a card footer renders teal. Centralising the cascade here makes
 * that bug uncatchable by passing-in inputs.
 *
 * @example
 *   const colour = getAvatarColorForUser({ userId: user.id, email: user.email, name: user.name });
 */
export function getAvatarColorForUser(user: {
  userId?: string | null;
  email?: string | null;
  name?: string | null;
}): AvatarColor {
  return getAvatarColor(user.userId || user.email || user.name);
}
